class KitBuilder extends HTMLElement {
  #mode = "none";
  #currency = "";
  #personalisationPrice = 0;

  /** @type {Array<{ number: string, image: string }>} */
  #shirtNumbers = [];
  /** @type {Array<{ id: number, name: string, number: number }>} */
  #players = [];

  // Preview Image Data
  #backShirtImage = "";
  #characterLimit = 12;
  #nameFont = "monospace";
  #nameColor = "black";
  #offsets = {
    name: 0,
    number: 0,
  };
  #imageScale = 1;
  #imageVerticalOffset = 0;
  #nameScale = 1;
  #numberScale = 1;
  #curvedNameRadius = 180;

  /**
   * @type {{
   *   playerSelect: HTMLSelectElement | null,
   *   nameInput: HTMLInputElement | null,
   *   numberInput: HTMLInputElement | null,
   *   previewImage: HTMLImageElement | null,
   *   personalisedTypeInput: HTMLInputElement | null | undefined,
   *   personalisedPriceInput: HTMLInputElement | null | undefined,
   *   personalisedInput: HTMLInputElement | null | undefined,
   *   customBuilder: HTMLElement | null,
   *   playerBuilder: HTMLElement | null,
   *   productForm: HTMLFormElement | null,
   * }}
   */
  elements = {
    playerSelect: null,
    nameInput: null,
    numberInput: null,
    previewImage: null,
    personalisedTypeInput: null,
    personalisedPriceInput: null,
    personalisedInput: null,
    customBuilder: null,
    playerBuilder: null,
    productForm: null,
  };

  /** @type {KitPreviewRenderer | null} */
  #previewRenderer = null;

  #kitBuilderInputNames = [
    "properties[Personalised]",
    "properties[_personalisedType]",
    "properties[_personalisePrice]",
    "properties[Badge]",
    "properties[_badgePrice]",
    "properties[Name]",
    "properties[Number]",
  ];

  // Bound event handlers for cleanup
  /**
   * Creates a KitBuilder instance.
   */
  constructor() {
    super();
  }

  connectedCallback() {
    this.#initState();
  


    // Create preview renderer
    this.#previewRenderer = new KitPreviewRenderer(this, {
      backShirtImage: this.#backShirtImage,
      shirtNumbers: this.#shirtNumbers,
      nameFont: this.#nameFont,
      nameColor: this.#nameColor,
      offsets: this.#offsets,
      imageScale: this.#imageScale,
      imageVerticalOffset: this.#imageVerticalOffset,
      nameScale: this.#nameScale,
      numberScale: this.#numberScale,
      curvedNameRadius: this.#curvedNameRadius,
    });

    this.#appendInputsToForm();
    this.#cacheElements();
    this.#handleModeChange("none");
    this.#addEventListeners();
    this.#setupFormValidation();
  }

  disconnectedCallback() {
    this.#removeEventListeners();
  }

  #removeEventListeners() {
    // Remove component event listeners
    this.removeEventListener("modechange", this.#onModeChange);
    this.removeEventListener("keyup", this.#handleInputKeyup);

    const nameInput = this.elements.nameInput;
    if (nameInput) {
      nameInput.removeEventListener("input", this.#nameInputChangeValidation);
    }
    const numberInput = this.elements.numberInput;
    if (numberInput) {
      numberInput.removeEventListener(
        "input",
        this.#numberInputChangeValidation,
      );
    }

    // Remove player select listener
    const playerSelect = this.elements.playerSelect;
    if (playerSelect) {
      playerSelect.removeEventListener("change", this.#onPlayerSelectChange);
    }

    // Remove form event listeners
    const productForm = this.elements.productForm;
    if (productForm) {
      productForm.removeEventListener("submit", this.#onFormSubmit, true);
    }

    // Remove document-level invalid listener
    document.removeEventListener("invalid", this.#onInvalid, true);
  }

  // ============================================
  // INITIALIZATION METHODS
  // ============================================

  /**
   * Reads data attributes from the <kit-builder> tag and populates private state.
   */
  #initState() {
    const d = this.dataset;

    this.#currency = d.currency ?? "";
    this.#personalisationPrice = parseFloat(d.personalisationPrice ?? "0");
    this.#backShirtImage = d.backShirtImage ?? "";
    this.#characterLimit = parseInt(d.characterLimit ?? "12");
    this.#nameFont = d.nameFont ?? "monospace";
    this.#nameColor = d.nameColor ?? "black";
    this.#imageScale = parseFloat(d.imageScale ?? "1");
    this.#imageVerticalOffset = parseInt(d.imageVerticalOffset ?? "0");
    this.#nameScale = parseFloat(d.nameScale ?? "1");
    this.#numberScale = parseFloat(d.numberScale ?? "1");
    this.#curvedNameRadius = parseInt(d.curvedNameRadius ?? "180");

    // Complex JSON Objects & Arrays
    this.#shirtNumbers = parseJSON(d.shirtNumbers, []);
    this.#players = parseJSON(d.players, []);

    // Offsets
    this.#offsets = {
      name: parseFloat(d.nameVerticalOffset ?? "0.32"),
      number: parseFloat(d.numberVerticalOffset ?? "0.26"),
    };
  }

  #cacheElements() {
    /** @type {HTMLFormElement | null} */
    const productForm = document.querySelector(
      'form[data-type="add-to-cart-form"]',
    );

    /** @type {HTMLInputElement | null} */
    const nameInput = this.querySelector('input[name="properties[Name]"]');
    /** @type {HTMLInputElement | null} */
    const numberInput = this.querySelector('input[name="properties[Number]"]');
    /** @type {HTMLSelectElement | null} */
    const playerSelect = this.querySelector("#playerSelect");
    /** @type {HTMLInputElement | null | undefined} */
    const personalisedTypeInput = productForm?.querySelector(
      'input[name="properties[_personalisedType]"]',
    );
    /** @type {HTMLInputElement | null | undefined} */
    const personalisedInput = productForm?.querySelector(
      'input[name="properties[Personalised]"]',
    );
    /** @type {HTMLInputElement | null | undefined} */
    const personalisedPriceInput = productForm?.querySelector(
      'input[name="properties[_personalisePrice]"]',
    );
    /** @type {HTMLElement | null} */
    const customBuilder = this.querySelector(".kit_builder__custom");
    /** @type {HTMLElement | null} */
    const playerBuilder = this.querySelector(".kit_builder__player");
    /** @type {HTMLImageElement | null} */
    const previewImage = this.querySelector("#preview-img");

    this.elements = {
      playerSelect,
      nameInput,
      numberInput,
      previewImage,
      personalisedTypeInput,
      personalisedPriceInput,
      personalisedInput,
      customBuilder,
      playerBuilder,
      productForm,
    };
  }

  #addEventListeners() {
    // Listen for mode changes from child component
    this.addEventListener("modechange", this.#onModeChange);

    // Input listeners for name/number fields
    this.addEventListener("keyup", this.#handleInputKeyup);

    // Player select change
    const playerSelect = this.elements.playerSelect;
    if (playerSelect) {
      playerSelect.addEventListener("change", this.#onPlayerSelectChange);
    }
  }

  // ============================================
  // EVENT HANDLERS (arrow functions for auto-binding)
  // ============================================

  /** @param {Event} e */
  #onModeChange = (e) => {
    e.stopPropagation();
    if (e instanceof CustomEvent) {
      this.#handleModeChange(e.detail.mode);
    }
  };

  #onPlayerSelectChange = () => {
    this.#updatePlayerProperties();
    this.#generatePreviewImage();
  };

  // ============================================
  // MODE CHANGE HANDLING
  // ============================================

  /**
   * Handles the state change logic received from the child component.
   * @param {string} mode - The new mode ('none', 'player', 'custom').
   */
  #handleModeChange(mode) {
    // Update internal state
    this.#mode = mode;

    // Reset to default prices
    this.#setDefaultProductPrice();

    // Clear name/number inputs for non-player modes
    if (mode !== "player") {
      const nameInput = this.elements.nameInput;
      const numberInput = this.elements.numberInput;
      if (nameInput instanceof HTMLInputElement) nameInput.value = "";
      if (numberInput instanceof HTMLInputElement) numberInput.value = "";
    }

    // Mode-specific logic
    if (mode === "none") {
      this.#clearInputs();
    } else if (mode === "player") {
      this.#updatePlayerProperties();
    }

    // Apply UI changes
    this.#showModeSection(mode);
    this.#updatePersonalisedValue(mode, this.#currency);
    this.#configureFormInputs(mode);

    this.#generatePreviewImage();
  }

  // ============================================
  // INPUT HANDLING
  // ============================================

  /**
   * Handles keyup events on input fields.
   * @param {Event} e
   */
  #handleInputKeyup(e) {
    if (!(e.target instanceof HTMLInputElement)) return;
    const input = e.target;
    if (input.matches('input[name="properties[Number]"]')) {
      input.value = this.#limitString(input.value, 2);
      this.#generatePreviewImage();
    } else if (input.matches('input[name="properties[Name]"]')) {
      input.value = this.#limitString(input.value, this.#characterLimit);
      this.#generatePreviewImage();
    }
  }

  /**
   * @param {string} str
   * @param {number} limit
   * @returns {string}
   */
  #limitString(str, limit) {
    if (!str) return "";
    return str.length <= limit ? str : str.substring(0, limit);
  }

  /**
   * Toggle the required attribute on the name and number inputs.
   * @param {boolean} required
   */
  #toggleCustomInputRequired(required) {
    const nameInput = this.elements.nameInput;
    const numberInput = this.elements.numberInput;

    if (nameInput instanceof HTMLInputElement) nameInput.required = required;
    if (numberInput instanceof HTMLInputElement)
      numberInput.required = required;
  }

  /**
   * @param {boolean} enable
   */
  #toggleFormInputs(enable) {
    this.#kitBuilderInputNames.forEach((name) => {
      /** @type {HTMLInputElement | null} */
      const formInput = document.querySelector(
        `input[name="${name}"][type="hidden"]`,
      );
      if (formInput) {
        formInput.disabled = !enable;
      }
    });
  }

  #clearInputs() {
    const productForm = this.elements.productForm;

    this.#kitBuilderInputNames.forEach((name) => {
      // Clear inputs inside the component
      /** @type {HTMLInputElement | null} */
      const componentInput = this.querySelector(`input[name="${name}"]`);
      if (componentInput instanceof HTMLInputElement) {
        componentInput.value = "";
      }

      // Clear hidden inputs in the product form
      if (productForm) {
        /** @type {HTMLInputElement | null} */
        const formInput = productForm.querySelector(
          `input[name="${name}"][type="hidden"]`,
        );
        if (formInput instanceof HTMLInputElement) {
          formInput.value = "";
        }
      }
    });
  }

  /**
   * Shows/hides the custom and player builder sections based on mode.
   * @param {string} mode - The mode ('none', 'player', 'custom').
   */
  #showModeSection(mode) {
    const customBuilder = this.elements.customBuilder;
    const playerBuilder = this.elements.playerBuilder;

    if (customBuilder instanceof HTMLElement) {
      customBuilder.style.display = mode === "custom" ? "block" : "none";
    }
    if (playerBuilder instanceof HTMLElement) {
      playerBuilder.style.display = mode === "player" ? "block" : "none";
    }
  }

  /**
   * Updates the personalised input display value and type.
   * @param {string} mode - The mode ('none', 'player', 'custom').
   * @param {string} currency - The currency symbol.
   */
  #updatePersonalisedValue(mode, currency = "£") {
    const personalisedInput = this.elements.personalisedInput;
    const personalisedPrice = this.elements.personalisedPriceInput;
    const personalisedTypeInput = this.elements.personalisedTypeInput;

    if (!(personalisedInput instanceof HTMLInputElement)) return;

    const price =
      personalisedPrice instanceof HTMLInputElement
        ? personalisedPrice.value
        : "";

    switch (mode) {
      case "none":
        personalisedInput.value = "None";
        break;
      case "custom":
        personalisedInput.value = `Custom (${currency}${price})`;
        break;
      case "player":
        personalisedInput.value = `Player (${currency}${price})`;
        break;
    }

    if (personalisedTypeInput instanceof HTMLInputElement) {
      // Capitalize first letter: "none" -> "None", "custom" -> "Custom"
      personalisedTypeInput.value =
        mode.charAt(0).toUpperCase() + mode.slice(1);
    }
  }

  /**
   * Configures form input states based on mode.
   * @param {string} mode - The mode ('none', 'player', 'custom').
   */
  #configureFormInputs(mode) {
    const requireInputs = mode === "custom";
    const enableInputs = mode !== "none";

    this.#toggleCustomInputRequired(requireInputs);
    this.#toggleFormInputs(enableInputs);
  }

  // ============================================
  // PLAYER HANDLING
  // ============================================

  #updatePlayerProperties() {
    const playerSelect = this.elements.playerSelect;

    const playerID = playerSelect?.value;
    const player = this.#players.find((obj) => obj.id === Number(playerID));

    if (player) {
      const nameInput = this.elements.nameInput;
      const numberInput = this.elements.numberInput;

      if (nameInput instanceof HTMLInputElement) {
        nameInput.value = player.name.toUpperCase();
        const hiddenNameInput = this.elements.productForm?.querySelector(
          'input[name="properties[Name]"]',
        );
        if (hiddenNameInput instanceof HTMLInputElement) {
          hiddenNameInput.value = player.name.toUpperCase();
        }
      }
      if (numberInput instanceof HTMLInputElement) {
        numberInput.value = player.number.toString();
        const hiddenNumberInput = this.elements.productForm?.querySelector(
          'input[name="properties[Number]"]',
        );
        if (hiddenNumberInput instanceof HTMLInputElement) {
          hiddenNumberInput.value = player.number.toString();
        }
      }
    }
  }

  // ============================================
  // PRICE HANDLING
  // ============================================

  #setDefaultProductPrice() {
    const personalisedPriceInput = this.elements.personalisedPriceInput;
    if (personalisedPriceInput instanceof HTMLInputElement) {
      personalisedPriceInput.value = String(this.#personalisationPrice);
    }

    // Deselect all badge buttons
    /** @type {NodeListOf<HTMLInputElement>} */
    const badgeBtns = this.querySelectorAll(".kit-badge > input");
    badgeBtns.forEach((btn) => {
      btn.checked = btn.value === "none";
    });

    /** @type {HTMLInputElement | null | undefined} */
    const badgeInput = this.elements.productForm?.querySelector(
      'input[name="properties[Badge]"]',
    );
    /** @type {HTMLInputElement | null | undefined} */
    const badgePriceInput = this.elements.productForm?.querySelector(
      'input[name="properties[_badgePrice]"]',
    );

    if (badgeInput) badgeInput.value = "";
    if (badgePriceInput) badgePriceInput.value = "";

    const badgeAddonPrices = this.querySelectorAll(
      '[id*="kit_badge-addon-price-"]',
    );
    badgeAddonPrices.forEach((el) => {
      el.textContent = "";
    });
  }

  // ============================================
  // FORM HANDLING
  // ============================================

  /**
   * Creates hidden inputs inside the product form for kit builder properties.
   */
  #appendInputsToForm() {
    const productForm = document.querySelector(
      'form[data-type="add-to-cart-form"]',
    );
    if (!productForm) return;

    const kitBuilderInputs = [
      {
        name: "properties[Personalised]",
        sourceSelector: 'input[name="properties[Personalised]"]',
      },
      {
        name: "properties[_personalisedType]",
        sourceSelector: 'input[name="properties[_personalisedType]"]',
      },
      {
        name: "properties[_personalisePrice]",
        sourceSelector: 'input[name="properties[_personalisePrice]"]',
        defaultValue: String(this.#personalisationPrice),
      },
      {
        name: "properties[Badge]",
        sourceSelector: 'input[name="properties[Badge]"]',
      },
      {
        name: "properties[_badgePrice]",
        sourceSelector: 'input[name="properties[_badgePrice]"]',
      },
      {
        name: "properties[Name]",
        sourceSelector: 'input[name="properties[Name]"]',
        sync: true,
      },
      {
        name: "properties[Number]",
        sourceSelector: 'input[name="properties[Number]"]',
        sync: true,
      },
    ];

    kitBuilderInputs.forEach((config) => {
      /** @type {HTMLInputElement | null} */
      let formInput = productForm.querySelector(
        `input[name="${config.name}"][type="hidden"]`,
      );

      if (!formInput) {
        formInput = document.createElement("input");
        formInput.type = "hidden";
        formInput.name = config.name;
        if (config.defaultValue) {
          formInput.value = config.defaultValue;
        }
        productForm.appendChild(formInput);
      }

      const sourceInput = this.querySelector(config.sourceSelector);
      if (sourceInput instanceof HTMLInputElement) {
        formInput.value = sourceInput.value;

        if (config.sync) {
          sourceInput.addEventListener("input", () => {
            formInput.value = sourceInput.value;
          });
          sourceInput.addEventListener("blur", () => {
            formInput.value = sourceInput.value;
          });
        }
      }
    });
  }

  // ============================================
  // FORM VALIDATION
  // ============================================

  #setupFormValidation() {
    const nameInput = this.elements.nameInput;
    const numberInput = this.elements.numberInput;

    if (nameInput instanceof HTMLInputElement) {
      nameInput.addEventListener("blur", this.#validateInputs);
      nameInput.addEventListener("input", this.#nameInputChangeValidation);
    }
    if (numberInput instanceof HTMLInputElement) {
      numberInput.addEventListener("blur", this.#validateInputs);
      numberInput.addEventListener("input", this.#numberInputChangeValidation);
    }

    // Hook into form submission
    const productForm = this.elements.productForm;
    if (productForm) {
      productForm.addEventListener("submit", this.#onFormSubmit, true);
    }

    // Disable default form validation popup
    document.addEventListener("invalid", this.#onInvalid, true);
  }

  #nameInputChangeValidation = () => {
    const nameInput = this.elements.nameInput;

    const nameError = this.querySelector("#name-error");
    if (nameInput?.classList.contains("invalid")) {
      this.#clearFieldError(nameInput, nameError);
    }
  };

  #numberInputChangeValidation = () => {
    const numberInput = this.elements.numberInput;

    const numberError = this.querySelector("#number-error");
    if (numberInput?.classList.contains("invalid")) {
      this.#clearFieldError(numberInput, numberError);
    }
  };

  /** @param {Event} e */
  #onFormSubmit = (e) => {
    const customSection = this.elements.customBuilder;
    if (
      customSection instanceof HTMLElement &&
      customSection.style.display !== "none"
    ) {
      if (!this.#validateInputs()) {
        e.preventDefault();
        return false;
      }
    }
  };

  /** @param {Event} e */
  #onInvalid = (e) => {
    if (!(e.target instanceof HTMLInputElement)) return;

    if (
      e.target.name === "properties[Name]" ||
      e.target.name === "properties[Number]"
    ) {
      e.preventDefault();
      this.#validateInputs();
    }
  };

  /**
   * @returns {boolean}
   */
  #validateInputs = () => {
    let isValid = true;

    const nameInput = this.elements.nameInput;
    const numberInput = this.elements.numberInput;

    if (
      !(nameInput instanceof HTMLInputElement) ||
      !(numberInput instanceof HTMLInputElement)
    ) {
      return false;
    }

    const nameError = this.querySelector("#name-error");
    const numberError = this.querySelector("#number-error");

    // Clear previous errors
    this.#clearFieldError(nameInput, nameError);
    this.#clearFieldError(numberInput, numberError);

    const customBuilder = this.elements.customBuilder;
    if (
      !(customBuilder instanceof HTMLElement) ||
      customBuilder.style.display === "none"
    ) {
      return true;
    }

    // Validate name
    const nameValue = nameInput.value.trim();
    if (!nameValue) {
      this.#showFieldError(nameInput, nameError, "Name is required");
      isValid = false;
    } else if (nameValue.length > this.#characterLimit) {
      this.#showFieldError(
        nameInput,
        nameError,
        `Name cannot exceed ${this.#characterLimit} characters`,
      );
      isValid = false;
    }

    // Validate number
    const numberValue = parseInt(numberInput.value);
    if (isNaN(numberValue)) {
      this.#showFieldError(numberInput, numberError, "Number is required");
      isValid = false;
    } else if (numberValue < 0 || numberValue > 99) {
      this.#showFieldError(
        numberInput,
        numberError,
        "Number must be between 0 and 99",
      );
      isValid = false;
    }

    return isValid;
  };

  /**
   * @param {Element} inputElement
   * @param {Element | null} errorElement
   * @param {string} message
   */
  #showFieldError(inputElement, errorElement, message) {
    if (!(inputElement instanceof HTMLElement)) return;

    inputElement.classList.add("invalid");
    if (errorElement instanceof HTMLElement) {
      const errorText = errorElement.querySelector(".error-text");
      if (errorText) {
        errorText.textContent = message;
      }
      errorElement.classList.add("show");
    }
  }

  /**
   * @param {Element} inputElement
   * @param {Element | null} errorElement
   */
  #clearFieldError(inputElement, errorElement) {
    if (!(inputElement instanceof HTMLElement)) return;

    inputElement.classList.remove("invalid");
    if (errorElement instanceof HTMLElement) {
      errorElement.classList.remove("show");
    }
  }

  // ============================================
  // PREVIEW IMAGE GENERATION
  // ============================================

  async #generatePreviewImage() {
    if (!this.#previewRenderer) return;

    let name = "";
    let number = null;

    switch (this.#mode) {
      case "custom":
        name = this.elements.nameInput?.value ?? "";
        number = this.elements.numberInput?.value ?? "";
        break;
      case "player":
        const playerID = this.elements.playerSelect?.value;
        const player = this.#players.find((obj) => obj.id === Number(playerID));
        if (player) {
          name = player.name.toUpperCase();
          number = player.number;
        }
        break;
    }

    await this.#previewRenderer.render(name, number, this.#mode);
  }
}

// Register the custom element
customElements.define("kit-builder", KitBuilder);
